import type { SvgIconComponent } from '@mui/icons-material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box } from '@mui/material';
import React from 'react';

/** Same purpose → icon mapping as ProgramProgressStepper / Coach. */
export const PURPOSE_ICONS: Record<string, SvgIconComponent> = {
  feedback_report: AssessmentIcon,
  talking_points: SpeakerNotesIcon,
  curriculum: LibraryBooksIcon,
  lesson_plan: MenuBookIcon,
  growth_report_week_4: TrendingUpIcon,
  growth_report_week_8: TrendingUpIcon,
  growth_report_week_12: TrendingUpIcon,
  pre_assessment: QuizIcon,
  post_assessment: QuizIcon,
};

type CompletedContentIconProps = {
  purpose: string;
  /** Outer icon size in px. Default 18. */
  size?: number;
};

/**
 * Content-type icon with a small completion check badge — used on Dashboard
 * summary rows so completed items stay scannable by kind, not just by checkmark.
 */
const CompletedContentIcon: React.FC<CompletedContentIconProps> = ({
  purpose,
  size = 18,
}) => {
  const Icon = PURPOSE_ICONS[purpose] ?? AssessmentIcon;
  const badgeSize = Math.max(10, Math.round(size * 0.7));

  return (
    <Box
      component="span"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flexShrink: 0,
        mr: 0.25,
      }}
      aria-hidden
    >
      <Icon color="secondary" sx={{ fontSize: size }} />
      <CheckCircleIcon
        color="success"
        sx={{
          fontSize: badgeSize,
          position: 'absolute',
          right: -5,
          bottom: -4,
          bgcolor: 'background.paper',
          borderRadius: '50%',
        }}
      />
    </Box>
  );
};

export default CompletedContentIcon;
